import React, { useState, useRef, useEffect } from 'react'
import map from 'lodash/map'
import classnames from 'classnames'

const OTP_LENGTH = 6

interface OTPInputProps {
  readonly value: readonly string[]
  onChange(newValue: string[]): void
}

const OTPInput: React.FC<OTPInputProps> = (props) => {
  const { value, onChange } = props
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState<number>(0)

  useEffect(() => {
    const firstInput = inputRefs.current[0]
    if (firstInput) {
      firstInput.focus()
    }
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = Number(event.currentTarget.dataset.index)
    const inputValue = event.currentTarget.value
    const digit = inputValue.replace(/[^0-9]/g, '')

    if (digit.length === 0) {
      const updatedValue = [...value]
      updatedValue[index] = ''
      onChange(updatedValue)
      return
    }

    const lastDigit = digit.slice(-1)
    const newValue = [...value]
    newValue[index] = lastDigit
    onChange(newValue)

    if (index < OTP_LENGTH - 1) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const index = Number(event.currentTarget.dataset.index)

    if (event.key === 'Backspace') {
      if (value[index] === '' && index > 0) {
        const prevInput = inputRefs.current[index - 1]
        if (prevInput) {
          prevInput.focus()
        }
        const newValue = [...value]
        newValue[index - 1] = ''
        onChange(newValue)
      } else {
        const newValue = [...value]
        newValue[index] = ''
        onChange(newValue)
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = inputRefs.current[index - 1]
      if (prevInput) {
        prevInput.focus()
      }
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, OTP_LENGTH)
    if (pastedData.length > 0) {
      const newValue = [...value]
      for (let i = 0; i < pastedData.length && i < OTP_LENGTH; i++) {
        newValue[i] = pastedData[i]
      }
      onChange(newValue)
      const nextFocusIndex = Math.min(pastedData.length, OTP_LENGTH - 1)
      const targetInput = inputRefs.current[nextFocusIndex]
      if (targetInput) {
        targetInput.focus()
      }
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const index = Number(event.currentTarget.dataset.index)
    setFocusedIndex(index)
  }

  const handleBlur = () => {
    setFocusedIndex(-1)
  }

  const setInputRef = (el: HTMLInputElement | null, index: number) => {
    inputRefs.current[index] = el
  }

  const renderInputBox = (digit: string, index: number) => {
    const boxClassName = classnames(
      'OTPVerification__otp-box',
      {
        'OTPVerification__otp-box-focused': focusedIndex === index,
        'OTPVerification__otp-box-has-value': focusedIndex !== index && digit !== '',
      }
    )

    const handleRef = (el: HTMLInputElement | null) => {
      setInputRef(el, index)
    }

    return (
      <input
        key={index}
        ref={handleRef}
        className={boxClassName}
        type="text"
        inputMode="numeric"
        maxLength={2}
        value={digit}
        data-index={index}
        data-testid={`onboarding-otp-digit-${index + 1}`}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPaste={handlePaste}
        autoComplete="one-time-code"
      />
    )
  }

  return (
    <div className="OTPVerification__otp-container">
      {map(value, renderInputBox)}
    </div>
  )
}

export default OTPInput
